import { FlowGrid } from '@fs/zion-ui'
import React, { Fragment } from 'react'
import { useSlsStateContext } from '@fs/zion-sls'
import { useWatch, useZionFormContext } from '@fs/zion-form'
import PrimaryEventWrapper from './PrimaryEventWrapper'
import PrimaryEventEdit from './PrimaryEventEdit'
import PrimaryEventView from './PrimaryEventView'
import EventForm from './EventForm'
import { useEventSuggestions } from '../hooks/useEventSuggestions'

/**
 * EventsSection
 *
 * Renders a set of events in either primary or secondary section based on the `isPrimarySection` prop.
 *
 * @param {Object} props
 * @param {Array<Object>} props.events - Field array items from `useFieldArray`, each representing an event.
 * @param {Object} props.formArrayMethods - Methods from `useFieldArray` used to manipulate the array (remove/append/etc.).
 * @param {Object} props.person - The person object associated with the events.
 * @param {boolean} [props.isPrimarySection=false] - Whether this is the primary event section (shows only primaryOnly events)
 * @param {string} [props.appName] - The application name for save operations
 * @param {string} [props.userId] - The user ID for save operations
 * @param {boolean} [props.saveCardDataToSls] - Whether to save card data to SLS
 * @param {Object} props.control - Form control from react-hook-form
 * @param {string} [props.splitScreenImageArk] - ARK of the image in split screen mode, used for event suggestions
 * @param {Array<Object>} props.latestsPrimaryEvents - The latest primary events for the section
 * @param {Function} props.setLatestsPrimaryEvents - Function to update the latest primary events
 * @param {number|null} props.editingPrimaryEventIndex - The index of the primary event being edited
 * @param {Function} props.setEditingPrimaryEventIndex - Function to set the editing primary event index
 * @returns {JSX.Element} Rendered form section for events.
 */
const EventsSection = ({
  events, // which are basically the events to show
  formArrayMethods,
  person,
  appName,
  userId,
  saveCardDataToSls,
  isPrimarySection = false,
  splitScreenImageArk,
  latestsPrimaryEvents,
  setLatestsPrimaryEvents,
  editingPrimaryEventIndex,
  setEditingPrimaryEventIndex,
}) => {
  const slsState = useSlsStateContext()
  const { types: slsTypes, images, currentImageArk } = slsState
  const { control, clearErrors } = useZionFormContext()

  const currentEvents = useWatch({ control, name: 'events' })

  // Get event suggestions for the autosuggest dropdown
  const eventAutosuggestOptions = useEventSuggestions({
    person,
    slsTypes,
    currentFormEvents: currentEvents,
    isPrimarySection,
    images,
    currentImageArk,
  })

  if (events.length === 0) return null

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters={isPrimarySection ? 'xs' : 'md'}>
      {events.map((event, index) => (
        <Fragment key={event?.id || index}>
          {event?.primary && (event?.existingEventId || editingPrimaryEventIndex === index) ? (
            // Existing primary event: gray wrapper with view/edit toggle
            <PrimaryEventWrapper>
              {editingPrimaryEventIndex === index ? (
                <PrimaryEventEdit
                  event={event}
                  index={index}
                  formArrayMethods={formArrayMethods}
                  eventAutosuggestOptions={eventAutosuggestOptions}
                  person={person}
                  appName={appName}
                  userId={userId}
                  saveCardDataToSls={saveCardDataToSls}
                  splitScreenImageArk={splitScreenImageArk}
                  onSave={() => setEditingPrimaryEventIndex(null)}
                  onCancel={() => setEditingPrimaryEventIndex(null)}
                  latestsPrimaryEvents={latestsPrimaryEvents}
                  setLatestsPrimaryEvents={setLatestsPrimaryEvents}
                />
              ) : (
                <PrimaryEventView
                  event={event}
                  onEdit={() => {
                    clearErrors()
                    setEditingPrimaryEventIndex(index)
                  }}
                  disableEdit={editingPrimaryEventIndex !== null}
                />
              )}
            </PrimaryEventWrapper>
          ) : (
            // New primary event OR additional event: no wrapper, just EventForm
            <EventForm
              event={event}
              index={index}
              formArrayMethods={formArrayMethods}
              eventAutosuggestOptions={eventAutosuggestOptions}
              person={person}
              appName={appName}
              userId={userId}
              saveCardDataToSls={saveCardDataToSls}
              splitScreenImageArk={splitScreenImageArk}
            />
          )}
        </Fragment>
      ))}
    </FlowGrid>
  )
}

export default EventsSection
